import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteCategory, listCategories, updateCategory } from '../../api/business/endpoints';
import type { Category, CategoryInput } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { usePermissions } from '../../auth/usePermissions';
import {
  filterTree,
  MAX_CATEGORY_DEPTH,
  flattenCategories,
  indentTarget,
  outdentTarget,
  siblingsOf,
} from '../../components/categoryTree';
import { CompanySelector } from '../../components/CompanySelector';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { FormDialog } from '../../components/FormDialog';
import { missingLanguages, textsOf } from '../../components/localisedText';
import { MissingLanguagesChip } from '../../components/MissingLanguagesChip';
import { PageHeader } from '../../components/PageHeader';
import { TableFilters } from '../../components/TableFilters';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useSelectedCompany } from '../../components/useSelectedCompany';
import { useLanguage } from '../../i18n/LanguageContext';

import { CategoryDialog } from './CategoryDialog';

type Dialog =
  | { kind: 'edit'; category: Category | null; parentId: string | null }
  | { kind: 'delete'; category: Category };

/**
 * The company's category tree (R0030 WP-06): expand and collapse, move a category up or down among its siblings,
 * nest it under the one above or take it a level out — its subtree with it — and see how many products each
 * holds, itself and with everything under it. Editing happens in a dialog with a tab per content language.
 */
export const CategoriesPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const selection = useSelectedCompany('products.read');
  const company = selection.company;
  const { languages } = useContentLanguages();
  const [term, setTerm] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [moveProducts, setMoveProducts] = useState(false);
  const canManage =
    company !== null && permissions.writable && permissions.can('products.manage', company.id);

  const query = useQuery({
    queryKey: ['categories', company?.id],
    enabled: company !== null,
    queryFn: () => listCategories(company?.id ?? ''),
  });
  const move = useAppMutation(
    ({ id, changes }: { id: string; changes: CategoryInput }) =>
      updateCategory(company?.id ?? '', id, changes),
    ['categories', 'product', 'products'],
  );
  const remove = useAppMutation(
    ({ id, moveToParent }: { id: string; moveToParent: boolean }) =>
      deleteCategory(company?.id ?? '', id, moveToParent),
    ['categories', 'product', 'products'],
    () => setDialog(null),
  );

  if (!selection.isLoading && company === null) {
    return (
      <>
        <PageHeader title={t('categories.title')} />
        <ErrorNotice error={selection.error} onRetry={selection.retry} />
        <Paper variant="outlined">
          <EmptyState
            title={t('products.noCompany')}
            detail={t('products.noCompanyDetail')}
            action={<Button onClick={() => navigate('/companies')}>{t('nav.companies')}</Button>}
          />
        </Paper>
      </>
    );
  }

  const tree = query.data ?? [];
  const needle = term.trim().toLocaleLowerCase('sk');
  const shown = filterTree(tree, needle);
  const everything = flattenCategories(tree);
  const busy = move.isPending || remove.isPending;

  const toggle = (id: string): void =>
    setCollapsed((previous) => {
      const next = new Set(previous);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });

  const row = (category: Category) => {
    const siblings = siblingsOf(tree, category);
    const index = siblings.findIndex((one) => one.id === category.id);
    const open = needle !== '' || !collapsed.has(category.id);
    const indent = indentTarget(tree, category);
    const outdent = outdentTarget(tree, category);

    return (
      <Box key={category.id}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5,
            pr: 1,
            pl: 1 + (category.depth - 1) * 3,
            borderBottom: 1,
            borderColor: 'divider',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <IconButton
            size="small"
            disabled={category.children.length === 0}
            aria-label={open ? t('categories.collapse') : t('categories.expand')}
            onClick={() => toggle(category.id)}
            sx={{ visibility: category.children.length === 0 ? 'hidden' : 'visible' }}
          >
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Typography sx={{ fontWeight: category.depth === 1 ? 600 : 500, flexGrow: 1, minWidth: 0 }} noWrap>
            {category.name}
            {category.active ? null : (
              <Chip size="small" variant="outlined" label={t('categories.inactive')} sx={{ ml: 1 }} />
            )}
            <MissingLanguagesChip
              missing={missingLanguages(textsOf(category.translations, 'name', languages), languages)}
            />
          </Typography>
          <Tooltip
            title={t('categories.productsHint', {
              direct: category.productCount,
              total: category.totalProductCount,
            })}
          >
            <Chip
              size="small"
              label={`${category.productCount} / ${category.totalProductCount}`}
              onClick={
                category.totalProductCount > 0
                  ? () => navigate(`/products?categoryId=${category.id}`)
                  : undefined
              }
            />
          </Tooltip>
          {canManage ? (
            <Stack direction="row" spacing={0.25} alignItems="center">
              <Tooltip title={t('common.moveUp')}>
                <span>
                  <IconButton
                    aria-label={t('common.moveUp')}
                    size="small"
                    disabled={busy || index <= 0}
                    onClick={() => move.mutate({ id: category.id, changes: { position: index - 1 } })}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.moveDown')}>
                <span>
                  <IconButton
                    aria-label={t('common.moveDown')}
                    size="small"
                    disabled={busy || index === siblings.length - 1}
                    onClick={() => move.mutate({ id: category.id, changes: { position: index + 1 } })}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('categories.outdent')}>
                <span>
                  <IconButton
                    aria-label={t('categories.outdent')}
                    size="small"
                    disabled={busy || outdent === null}
                    onClick={() => outdent && move.mutate({ id: category.id, changes: outdent })}
                  >
                    <FormatIndentDecreaseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('categories.indent')}>
                <span>
                  <IconButton
                    aria-label={t('categories.indent')}
                    size="small"
                    disabled={busy || indent === null}
                    onClick={() => indent && move.mutate({ id: category.id, changes: { parentId: indent } })}
                  >
                    <FormatIndentIncreaseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('categories.createChild')}>
                <span>
                  <IconButton
                    aria-label={t('categories.createChild')}
                    size="small"
                    disabled={category.depth >= MAX_CATEGORY_DEPTH}
                    onClick={() => setDialog({ kind: 'edit', category: null, parentId: category.id })}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                size="small"
                onClick={() => setDialog({ kind: 'edit', category, parentId: category.parentId })}
              >
                {t('common.edit')}
              </Button>
              <Button
                size="small"
                color="warning"
                onClick={() => {
                  setMoveProducts(false);
                  setDialog({ kind: 'delete', category });
                }}
              >
                {t('common.delete')}
              </Button>
            </Stack>
          ) : null}
        </Box>
        {open ? category.children.map(row) : null}
      </Box>
    );
  };

  const deleting = dialog?.kind === 'delete' ? dialog.category : null;

  return (
    <>
      <PageHeader
        title={t('categories.title')}
        subtitle={t('categories.subtitle')}
        action={
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CompanySelector
              companies={selection.companies}
              value={company}
              onChange={(companyId) => selection.select(companyId)}
            />
            {canManage ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialog({ kind: 'edit', category: null, parentId: null })}
              >
                {t('categories.create')}
              </Button>
            ) : null}
          </Stack>
        }
      />
      <ErrorNotice
        error={query.error ?? selection.error ?? move.error}
        onRetry={() => void query.refetch()}
      />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('categories.searchHint')}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <Button size="small" onClick={() => setCollapsed(new Set())}>
            {t('categories.expandAll')}
          </Button>
          <Button
            size="small"
            onClick={() =>
              setCollapsed(
                new Set(
                  everything.filter((one) => one.category.children.length > 0).map((one) => one.category.id),
                ),
              )
            }
          >
            {t('categories.collapseAll')}
          </Button>
        </TableFilters>
        {query.isLoading || selection.isLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton height={32} />
            <Skeleton height={32} />
            <Skeleton height={32} />
          </Box>
        ) : (
          shown.map(row)
        )}
        {!query.isLoading && company !== null && shown.length === 0 ? (
          <EmptyState
            title={t('categories.empty')}
            detail={canManage ? t('categories.emptyManage') : t('categories.emptyRead')}
            action={
              canManage ? (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setDialog({ kind: 'edit', category: null, parentId: null })}
                >
                  {t('categories.create')}
                </Button>
              ) : undefined
            }
          />
        ) : null}
      </Paper>

      {dialog?.kind === 'edit' && company ? (
        <CategoryDialog
          companyId={company.id}
          tree={tree}
          category={dialog.category}
          parentId={dialog.parentId}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {deleting ? (
        <FormDialog
          title={t('categories.deleteTitle', { name: deleting.name })}
          onClose={() => setDialog(null)}
          onSubmit={() => remove.mutate({ id: deleting.id, moveToParent: moveProducts })}
          submitLabel={t('common.delete')}
          isBusy={
            remove.isPending || deleting.children.length > 0 || (deleting.productCount > 0 && !moveProducts)
          }
          error={remove.error}
          maxWidth="xs"
        >
          <Typography variant="body2">
            {deleting.children.length > 0
              ? t('categories.deleteChildren', { count: deleting.children.length })
              : t('categories.deleteMessage')}
          </Typography>
          {deleting.children.length === 0 && deleting.productCount > 0 ? (
            <FormControlLabel
              control={
                <Checkbox
                  checked={moveProducts}
                  onChange={(event) => setMoveProducts(event.target.checked)}
                />
              }
              label={
                deleting.parentId === null
                  ? t('categories.deleteMoveTop', { count: deleting.productCount })
                  : t('categories.deleteMove', { count: deleting.productCount })
              }
            />
          ) : null}
        </FormDialog>
      ) : null}
    </>
  );
};
